import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionData {
  id: string;
  email: string;
  subscription_status: string;
  ui_status: string;
  trial_end_date: string | null;
  subscription_end_date: string | null;
  market_region: string;
  preferred_language: string;
}

interface PaymentTokenHandlerProps {
  paymentToken: string | null;
  onDataLoaded: (data: SubscriptionData | null) => void;
  onLoading: (loading: boolean) => void;
}

export const PaymentTokenHandler = ({ paymentToken, onDataLoaded, onLoading }: PaymentTokenHandlerProps) => {
  const { toast } = useToast();
  const hasValidatedRef = useRef(false);

  const stableOnDataLoaded = useCallback(onDataLoaded, []);
  const stableOnLoading = useCallback(onLoading, []);

  useEffect(() => {
    if (!paymentToken || hasValidatedRef.current) return;

    let cancelled = false;

    const validateToken = async () => {
      try {
        console.log('[PaymentTokenHandler] Validating payment token');
        
        // Capture campaign ref from current URL
        const urlParams = new URLSearchParams(window.location.search);
        const campaignRef = urlParams.get('ref') || 'direct';
        
        const { data, error } = await supabase.functions.invoke('validate-payment-token', {
          body: { 
            payment_token: paymentToken,
            campaign_ref: campaignRef
          }
        });

        if (error) {
          console.error('[PaymentTokenHandler] Validation error:', error);
          console.log('[PaymentTokenHandler] Token inválido, redirecionando para /preferences');
          localStorage.removeItem('payment_session');
          window.location.href = '/preferences';
          return;
        }

        // Check if token is invalid
        if (!data?.valid) {
          console.log('[PaymentTokenHandler] Token inválido, redirecionando para /preferences');
          localStorage.removeItem('payment_session');
          window.location.href = '/preferences';
          return;
        }

        if (data?.valid && data?.subscription) {
          console.log('[PaymentTokenHandler] Token validated successfully');
          
          // Try to exchange payment token for session token
          const { data: exchangeData, error: exchangeError } = await supabase.functions.invoke('exchange-payment-token', {
            body: { payment_token: paymentToken }
          });

          if (exchangeError || !exchangeData?.success) {
            // Check if it's a used payment link or invalid token error
            if (exchangeData?.error === 'PAYMENT_LINK_ALREADY_USED' || exchangeData?.error === 'INVALID_PAYMENT_TOKEN') {
              console.log('[PaymentTokenHandler] Token inválido ou já utilizado, redirecionando para /preferences');
              localStorage.removeItem('payment_session');
              window.location.href = '/preferences';
              return;
            }
            
            console.error('[PaymentTokenHandler] Failed to exchange token:', exchangeError);
            toast({
              title: "Erro de autenticação",
              description: exchangeData?.message || "Não foi possível autenticar com este link.",
              variant: "destructive"
            });
            if (!cancelled) {
              stableOnDataLoaded(null);
            }
            return;
          }

          console.log('[PaymentTokenHandler] Token exchanged successfully');
          
          if (!cancelled) {
            stableOnDataLoaded(data.subscription);
          }
          
          // Store session data from the exchange
          const sessionData = {
            session_token: exchangeData.session.token,
            subscription_id: exchangeData.user.id,
            email: exchangeData.user.email,
            expires_at: exchangeData.session.expires_at,
            scope: 'full_access'
          };
          
          localStorage.setItem('payment_session', JSON.stringify(sessionData));
          
          // Clean URL by removing query parameters
          const url = new URL(window.location.href);
          url.search = ''; // Remove all query parameters
          window.history.replaceState({}, '', url.toString());
          
          // Dispatch custom event to notify other components
          window.dispatchEvent(new CustomEvent('paymentSessionReady', { 
            detail: sessionData 
          }));
          
          console.log('[PaymentTokenHandler] Session established and URL cleaned');
        } else {
          console.log('[PaymentTokenHandler] Invalid token response');
          toast({
            title: "Link inválido", 
            description: "Este link de pagamento não pôde ser validado.",
            variant: "destructive"
          });
          if (!cancelled) {
            stableOnDataLoaded(null);
          }
        }
      } catch (error: any) {
        console.error('[PaymentTokenHandler] Unexpected error:', error);
        toast({
          title: "Erro de conexão",
          description: "Não foi possível validar o link. Tente novamente.",
          variant: "destructive"
        });
        if (!cancelled) {
          stableOnDataLoaded(null);
        }
      } finally {
        if (!cancelled) {
          stableOnLoading(false);
        }
        hasValidatedRef.current = true;
      }
    };

    stableOnLoading(true);
    validateToken();

    return () => {
      cancelled = true;
    };
  }, [paymentToken, stableOnDataLoaded, stableOnLoading, toast]);

  return null; // This is a logic-only component
};